import { useState, useEffect, useRef } from 'react';
import { Search, Filter, X, Calendar, DollarSign, Tag, Mic, MicOff, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORIES = [
  "All", "Office Supplies", "Software & Subscriptions", "Travel & Transport",
  "Utilities", "Marketing & Advertising", "Professional Services",
  "Equipment & Hardware", "Food & Entertainment", "Insurance",
  "Rent & Facilities", "Communication", "Other"
];

function SearchFilter({ onSearch, onFilterChange, onAISearch }) {
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [searchMode, setSearchMode] = useState('type'); // 'type' or 'speak'
  const recognitionRef = useRef(null);
  const [filters, setFilters] = useState({
    vendor: '',
    category: 'All',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    status: ''
  });

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setQuery(transcript);
        
        // Auto-search after speech ends with final result
        if (event.results[0].isFinal) {
          setTimeout(() => {
            if (onAISearch) {
              onAISearch(transcript);
            } else {
              onSearch(transcript);
            }
          }, 500);
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };
    }
  }, [onSearch, onAISearch]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      setQuery('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleSearch = () => {
    onSearch(query);
  };

  const handleAISearch = () => {
    if (onAISearch) {
      onAISearch(query);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      vendor: '',
      category: 'All',
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: '',
      status: ''
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => 
    value && value !== 'All' && value !== ''
  );

  return (
    <div className="space-y-3">
      {/* Search Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => setSearchMode('type')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              searchMode === 'type' 
                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            Type
          </button>
          <button
            onClick={() => setSearchMode('speak')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
              searchMode === 'speak' 
                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            <Mic className="w-3 h-3" />
            Speak
          </button>
        </div>
        {onAISearch && (
          <button
            onClick={handleAISearch}
            disabled={!query.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors disabled:opacity-50"
            title="Ask AI about this"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Ask AI
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isListening ? "Listening..." : searchMode === 'speak' ? "Click mic to speak..." : "Search invoices, vendors..."}
            className={`w-full pl-10 pr-12 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 ${
              isListening ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-slate-200'
            }`}
            data-testid="search-input"
          />
          {/* Voice Button inside input */}
          <button
            onClick={toggleListening}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${
              isListening 
                ? 'bg-red-500 text-white animate-pulse' 
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
            title={isListening ? 'Stop listening' : 'Voice search'}
            data-testid="voice-search-btn"
          >
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSearch}
            className="px-4 py-2.5 bg-indigo-700 text-white rounded-xl font-medium hover:bg-indigo-800 transition-colors text-sm"
            data-testid="search-button"
          >
            Search
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2.5 border rounded-xl font-medium transition-colors text-sm flex items-center space-x-2 ${
              hasActiveFilters 
                ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300' 
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="filter-toggle"
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm">Filters</h4>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 flex items-center space-x-1"
                  >
                    <X className="w-3 h-3" />
                    <span>Clear all</span>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Vendor */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Vendor</label>
                  <input
                    type="text"
                    value={filters.vendor}
                    onChange={(e) => handleFilterChange('vendor', e.target.value)}
                    placeholder="Vendor name"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none dark:bg-slate-700 dark:text-slate-200"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none bg-white dark:bg-slate-700 dark:text-slate-200"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat === 'All' ? '' : cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none bg-white dark:bg-slate-700 dark:text-slate-200"
                  >
                    <option value="">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Date Range</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="flex-1 px-2 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-xs focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none dark:bg-slate-700 dark:text-slate-200"
                    />
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="flex-1 px-2 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-xs focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none dark:bg-slate-700 dark:text-slate-200"
                    />
                  </div>
                </div>

                {/* Amount Range */}
                <div className="sm:col-span-2 lg:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Amount Range</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="number"
                        value={filters.minAmount}
                        onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                        placeholder="Min"
                        className="w-full pl-7 pr-2 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none dark:bg-slate-700 dark:text-slate-200"
                      />
                    </div>
                    <span className="text-slate-400">-</span>
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="number"
                        value={filters.maxAmount}
                        onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                        placeholder="Max"
                        className="w-full pl-7 pr-2 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 outline-none dark:bg-slate-700 dark:text-slate-200"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SearchFilter;
